import axios, { AxiosInstance } from "axios";
import {
  AppId,
  INVITE_WORKSPACE_PARAM,
  NO_WORKSPACE_PARAM,
  endpoints,
} from "@songtradr-one/shared-common";
import { AppStorage } from "./app-storage";

let _axiosClient: AxiosInstance;
let _baseFrontendUrl: string;
let _appId: AppId;

/**
 * A helper function to initialize a frontend sso client to encapsulate sso related operations.
 *
 * @param appId - The ID of the consuming application.
 * @param baseUrl - The base URL for the Axios instance.
 * @param baseFrontendUrl - The base URL for the SSO frontend.
 *
 * @returns An object encapsulating sso related helpers & requests using the axios singleton.
 * @throws An error if:
 *         - The function is called without a `baseUrl` and the client has not been initialized.
 */
export const getSsoClient = (
  appId?: AppId,
  baseUrl?: string,
  baseFrontendUrl?: string,
) => {
  // Initialize...
  if (appId && baseUrl && baseFrontendUrl) {
    _appId = appId;
    _baseFrontendUrl = baseFrontendUrl;

    const currentOrgId = AppStorage.currentOrgId;
    const currentPath = AppStorage.getCurrentPath();
    const previousPath = AppStorage.previousPath;

    const newHeaders = {
      "x-app-id": appId,
      "x-request-utc": new Date().toISOString(),
      "x-device-id": AppStorage.deviceId,
      "x-session-id": AppStorage.sessionId,
      ...(currentOrgId ? { "x-current-org-id": currentOrgId } : {}),
      ...(currentPath !== previousPath ? { "x-client-path": currentPath } : {}),
    };

    _axiosClient = axios.create({
      baseURL: baseUrl,
      timeout: 60000,
      headers: newHeaders,
      withCredentials: true,
    });
  } else if (!_axiosClient || !_appId || !_baseFrontendUrl) {
    throw new Error(
      "Frontend sso client is not properly initialized. Please provide the proper arguments to getSso()",
    );
  }

  // Get Frontend URL with optional workspace prefix
  const getFrontendUrl = (_workspace?: string): string => {
    const url = new URL(_baseFrontendUrl);
    if (_workspace) {
      const parts = url.hostname.split(".");
      url.hostname = `${_workspace}.${parts.join(".")}`;
    }
    return url.toString().replace(/\/$/, ""); // remove trailing slash
  };

  // SSO related backend requests
  const requests = {
    refreshToken: () => {
      return _axiosClient.post(endpoints.auth.refresh);
    },

    signOut: () => {
      AppStorage.clear(); // Clear app storage on sign out
      return _axiosClient.post(endpoints.auth.signOut);
    },
  };

  // Flag carried by invite links that belong to no workspace, so sso can send
  // the user straight to sign-in instead of asking for a workspace name
  const hasNoWorkspaceParam = (): boolean =>
    new URLSearchParams(window.location.search).get(NO_WORKSPACE_PARAM) ===
    "true";

  // SSO related redirects
  const redirects = {
    signIn: async (returnUrl?: string, _workspace?: string) => {
      const url = new URL(getFrontendUrl(_workspace));
      url.searchParams.set("src", _appId);
      if (!_workspace && hasNoWorkspaceParam())
        url.searchParams.set(NO_WORKSPACE_PARAM, "true");
      url.searchParams.set("follow", returnUrl ?? window.location.href);

      window.location.assign(url.toString());
      await new Promise(() => {
        // Intentionally never resolving to freeze execution before redirect is executed
      });
    },

    signOut: async (returnUrl?: string, _orgId?: string) => {
      const url = new URL(`${getFrontendUrl()}/sign-out`);
      url.searchParams.set("src", _appId);
      url.searchParams.set("follow", returnUrl ?? window.location.href);

      window.location.assign(url.toString());
      await new Promise(() => {
        // Intentionally never resolving to freeze execution before redirect is executed
      });
    },

    manageAccount: async () => {
      window.location.assign(`${getFrontendUrl("account")}`);
      await new Promise(() => {
        // Intentionally never resolving to freeze execution before redirect is executed
      });
    },
  };

  // SSO related actions
  const actions = {
    //CLEAR OLD-STUDIO COOKIES
    /**@todo remove once old studio/AuxClients is deprecated */
    clearOldStudioCookies: () => {
      document.cookie.split(";").forEach((cookie) => {
        const name = cookie.split("=")[0].trim();
        if (
          name.startsWith("CognitoIdentityServiceProvider.") ||
          name === "portal-organisationId" ||
          name === "tsb_ignore"
        ) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=.studio.massivemusic.com`;
        }
      });
    },
    signOut: async (returnUrl?: string) => {
      //SIGN OUT SSO
      await requests.signOut();

      //CLEAR OLD-STUDIO COOKIES
      /**@todo remove once old studio/AuxClients is deprecated */
      actions.clearOldStudioCookies();

      //REDIRECT TO SIGN OUT FRONTEND
      await redirects.signOut(returnUrl);
    },
    signInWithWorkspace: async (returnUrl?: string) => {
      // CHECK FOR INVITE WORKSPACE PARAM
      const urlParams = new URLSearchParams(window.location.search);
      const workspace =
        urlParams.get(INVITE_WORKSPACE_PARAM) ??
        AppStorage.workspace ??
        undefined;

      // Remove invite_ws param from return URL to prevent it from persisting after sign-in
      if (returnUrl && workspace) {
        const url = new URL(returnUrl);
        url.searchParams.delete(INVITE_WORKSPACE_PARAM);
        returnUrl = url.toString();
      }

      await redirects.signIn(returnUrl, workspace);
    },
  };

  return {
    requests,
    redirects,
    actions,
  };
};
